import { forwardRef, ReactElement, useCallback, useMemo, useState } from 'react'
import { Text } from '../../Atoms'
import useElementSize from '../../Hooks/useElementSize'
import useEventListener from '../../Hooks/useEventListener'
import { AppBarProps } from './props'
import { FabIcon, StyledAppBar, Wrapper } from './styles'

const AppBar = forwardRef<HTMLDivElement, AppBarProps>(
    (
        {
            actionItems,
            elevateOnTop = false,
            floatingActionButton,
            hideOnScroll,
            hideThreshold = 50,
            navigationIcon,
            placement = 'top',
            show = true,
            title,
            variant = 'default',
        },
        ref
    ): ReactElement => {
        const [prevY, setPrevY] = useState(window.scrollY)
        const [removeShadow, setRemoveShadow] = useState(true)
        const [showBar, setShowBar] = useState(show)

        const [appBarRef, appBarSize] = useElementSize()
        const [fabRef, fabSize] = useElementSize()

        const onScroll = useCallback(
            (event: Event) => {
                const window = event.currentTarget as Window
                const scrollY = window.scrollY

                if (hideOnScroll) {
                    if (placement === 'top') {
                        if (hideThreshold > scrollY || prevY > scrollY) {
                            setShowBar(true)
                        } else if (prevY < scrollY) {
                            setShowBar(false)
                        }
                    } else {
                        if (prevY < scrollY) {
                            setShowBar(true)
                        } else if (prevY > scrollY) {
                            setShowBar(false)
                        }
                    }
                }

                if (elevateOnTop && scrollY === 0) {
                    setRemoveShadow(false)
                } else {
                    setRemoveShadow(true)
                }

                setPrevY(scrollY)
            },
            [elevateOnTop, hideOnScroll, hideThreshold, placement, prevY]
        )

        useEventListener('scroll', onScroll)

        const offset = useMemo(
            () => (showBar ? 0 : -appBarSize.height),
            [appBarSize.height, showBar]
        )
        const placementStyle = useMemo(
            () => (placement === 'top' ? { top: offset } : { bottom: offset }),
            [offset, placement]
        )

        return (
            <StyledAppBar
                ref={(node) => {
                    appBarRef(node)
                    if (typeof ref === 'function') {
                        ref(node)
                    } else if (ref) {
                        ref.current = node
                    }
                }}
                showShadow={showBar && removeShadow}
                style={placementStyle}
                variant={variant}
            >
                <Wrapper>
                    {navigationIcon}
                    {title && placement === 'top' && (
                        <Text size="xl">{title}</Text>
                    )}
                </Wrapper>
                {actionItems && <Wrapper>{actionItems}</Wrapper>}
                {floatingActionButton && placement === 'bottom' && (
                    <FabIcon
                        ref={fabRef}
                        style={{
                            bottom: appBarSize.height - fabSize.height / 2,
                        }}
                    >
                        {floatingActionButton}
                    </FabIcon>
                )}
            </StyledAppBar>
        )
    }
)

AppBar.displayName = 'AppBar'

export default AppBar

export { StyledAppBar }
